import type { TablePaginationConfig } from "antd/es/table";
import { useEffect, useState } from "react";
import { Pagination } from "../../repository/data.default";

/**
 * Server-paged triage table state shared by the dashboard cards: the page/rows/total/loading
 * quartet, the reset back to the first (worst) page on any scope change, the cancelled-flag
 * fetch effect and the antd `pagination` config.
 *
 * `fetchPage` reads the current scope off its closure — it is re-invoked whenever `scopeDeps`
 * change, so keep `scopeDeps` exactly the scope values that re-rank the table.
 */
export function useServerPagedTable<TRow>(
  fetchPage: (page: number) => Promise<Pagination<TRow>>,
  scopeDeps: readonly unknown[],
  pageSize: number,
) {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<TRow[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);

  // A scope change re-ranks the table, so it starts back at the first (worst) page.
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, scopeDeps);

  // The table's server page — fetched on mount, on scope changes and on pagination.
  useEffect(() => {
    let cancelled = false;
    setTableLoading(true);
    fetchPage(page)
      .then((result) => {
        if (cancelled) return;
        setRows(result.data);
        setTotalRecords(result.totalRecords);
        setTableLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotalRecords(0);
          setTableLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...scopeDeps, page]);

  const pagination: TablePaginationConfig = {
    current: page + 1,
    pageSize,
    total: totalRecords,
    hideOnSinglePage: true,
    onChange: (p) => setPage(p - 1),
  };

  return { rows, totalRecords, tableLoading, pagination };
}
